import { createConsumer } from '@rails/actioncable';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

// Keep this safely below OnlineStatusTracker::PRESENCE_DURATION (default 20s)
// to avoid edge-case expirations due to timer drift.
const PRESENCE_INTERVAL = 15000;
const RECONNECT_INTERVAL = 1000;

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(app, pubsubToken, websocketHost = '') {
    const normalizeWebsocketURL = host => {
      if (!host) return undefined;
      const h = String(host).trim().replace(/\/+$/, '');
      // Accept full urls like wss://x/ws/cable or relative paths like /ws
      if (h.endsWith('/cable')) return h;
      return `${h}/cable`;
    };

    const websocketURL = normalizeWebsocketURL(websocketHost);
    console.log('[ActionCable] Connecting to:', websocketURL || 'default URL');

    this.consumer = createConsumer(websocketURL);
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        connected: () => {
          console.log('[ActionCable] Connected successfully');
          // Run the reconnect flow directly from the connect event. The old
          // flow relied on a 1s polling timer (checkConnection) racing this
          // callback: this callback reset `isDisconnected` first, so the
          // poller's `isDisconnected && isOpen()` check almost never passed
          // and the reconnect resync (refetch of conversations/messages that
          // arrived while offline) frequently never ran — missed chats stayed
          // missing until a manual refresh.
          if (BaseActionCableConnector.isDisconnected) {
            BaseActionCableConnector.isDisconnected = false;
            this.clearReconnectTimer();
            this.onReconnect();
          }
        },
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        disconnected: () => {
          console.log('[ActionCable] Disconnected');
          BaseActionCableConnector.isDisconnected = true;
          this.onDisconnected();
          this.initReconnectTimer();
        },
        rejected: () => {
          console.error('[ActionCable] Connection rejected');
          BaseActionCableConnector.isDisconnected = true;
          this.onDisconnected();
          this.initReconnectTimer();
        },
      }
    );
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    this.presenceInterval = null;
    this.isAValidEvent = () => true;
    this.startPresenceInterval();
  }

  checkConnection() {
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected =
      BaseActionCableConnector.isDisconnected && isConnectionActive;
    if (isReconnected) {
      this.clearReconnectTimer();
      this.onReconnect();
      BaseActionCableConnector.isDisconnected = false;
    } else if (isConnectionActive) {
      // Already recovered (handled by the `connected` callback) — stop
      // polling instead of re-arming this timer forever.
      this.clearReconnectTimer();
    } else {
      this.initReconnectTimer();
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  clearPresenceInterval = () => {
    if (this.presenceInterval) {
      clearInterval(this.presenceInterval);
      this.presenceInterval = null;
    }
  };

  startPresenceInterval = () => {
    this.clearPresenceInterval();
    this.presenceInterval = setInterval(() => {
      const isConnectionActive = this.consumer?.connection?.isOpen?.();
      if (!isConnectionActive || !this.subscription) return;
      this.subscription.updatePresence();
    }, PRESENCE_INTERVAL);
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  disconnect() {
    this.clearReconnectTimer();
    this.clearPresenceInterval();
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    // One socket carries every account the user belongs to, and events for the
    // other accounts are dropped below instead of reaching the store. Tell
    // listeners which side of that line an event fell on, so "the socket is
    // alive" can be distinguished from "the account on screen is still live".
    const isCurrentAccount = this.isAValidEvent(data);

    try {
      emitter.emit(BUS_EVENTS.WEBSOCKET_EVENT_RECEIVED, {
        event,
        data,
        isCurrentAccount,
      });
    } catch (e) {
      // noop
    }
    if (isCurrentAccount) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
